
import React, {useMemo, useEffect, useState} from "react";
import {
    Search,
    MoreVertical,
    Users,
    UserCheck,
    UserX,
    FolderKanban,
    ChevronLeft,
    ChevronRight,
    Clock3,
    MapPin,
    MessageCircleMore,
    Smartphone, Download,
} from "lucide-react";

import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import {useNavigate, useParams} from "react-router-dom";
import CustomLoaderSmall from "../../components/Layout/CustomLoaderSmall";
import{ useSelector} from "react-redux";

import {toast} from "react-toastify";
import NavigationSideBar from "../../components/Layout/Graph & Tables/NavigationSideBar";
import DashboardHeader from "../../components/Layout/DashBoardHeader";
import StackCard from "../../components/Layout/StackCard";
import {useListProjectHoursPerWeekQuery,
        useGetCurrentWorkSessionQuery,
        useUpdateEmployeeHoursWorkedMutation,
     } from "../../features/attendanceApiSlice";

import AttendanceChatWindow from "../../../src/screens/mainscreen/AttendanceChatWindow";




/* ============================================================
   Component
============================================================ */

export default function EmployeeListScreen() {

    const navigate = useNavigate();

    const [selectedDate, setSelectedDate] = useState<string >(() => {
        return new Date().toISOString().split("T")[0];
    });

    const [weekOffset, setWeekOffset] = useState<number>(0);
    const {id} = useParams();

    const SELECTED_PROJECT_KEY = "ksolution_selected_project";

    const [selectedProject, setSelectedProject] = useState<string | null>(() => {
        return localStorage.getItem(SELECTED_PROJECT_KEY);
    });

    const [signInTime, setSignInTime] = useState<string>("");
    const [signOutTime, setSignOutTime] = useState<string>("");

    const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
    const [openHoursEditor, setOpenHoursEditor] = useState<boolean>(false);
    const [editingSession, setEditingSession] = useState<any>(null);
    const [isAttendanceChatOpen, setIsAttendanceChatOpen] = useState(false);

    useEffect(() => {
        const syncProjectId = () => {
            setSelectedProject(localStorage.getItem(SELECTED_PROJECT_KEY));
        };

        window.addEventListener("focus", syncProjectId);

        return () => {
            window.removeEventListener("focus", syncProjectId);
        }
    }, []);


    const {userInfo} = useSelector((state: any) => state).auth;
    const userId = userInfo?.id;

    const {data: currentSession } = useGetCurrentWorkSessionQuery(userId);

    const activeProjectName = currentSession?.userSession?.projectName ?? null;

    const {data: workHours, isLoading, refetch, isError} = useListProjectHoursPerWeekQuery(id);

    const [updateHours, {isLoading: isUpdateHours}] = useUpdateEmployeeHoursWorkedMutation();

    /* --------------------------------------------------------
       State
    -------------------------------------------------------- */

    const [searchTerm, setSearchTerm] = useState("");

    const [statusFilter, setStatusFilter] =
        useState<"All" | "Working" | "Off">("All");

    /* ========================================================
       Summary
    ======================================================== */

    const handlePreviousDay = () => {
        setSelectedDate((current) => {
            const date = new Date(`${current}T00:00:00`);
            date.setDate(date.getDate() - 1);

            return date.toISOString().split("T")[0];
        });
    };

    const handleNextDay = () => {
        setSelectedDate((current) => {
            const date = new Date(`${current}T00:00:00`);
            date.setDate(date.getDate() + 1);

            return date.toISOString().split("T")[0];
        });
    };

    const resetDate: number = 0;
    const setD = resetDate.toString();

    const handleCurrentDay = () => {
        const today = new Date();

        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");

        // Return to today's date
        setSelectedDate(`${year}-${month}-${day}`);

        // Reset navigation offset
        setWeekOffset(0);
    }

    const formattedSelectedDate = new Date(
        `${selectedDate}T00:00:00`
    ).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    /* ========================================================
       Filter Employees
    ======================================================== */


    /* ========================================================
       Pagination
    ======================================================== */


    const handleExport = () => {
        console.log("Export weekly timesheet");
    };

    /* ========================================================
       Helpers
    ======================================================== */

    const dailyTableRows = useMemo(() => {
        if (!Array.isArray(workHours) || !selectedDate) {
            return [];
        }

        const selectedDaySessions = workHours.filter((session: any) => {
            if (!session.userId || !session.signInTime) {
                return false;
            }

            const sessionDate = new Date(session.signInTime)
                .toISOString()
                .split("T")[0];

            return sessionDate === selectedDate;
        });

        const employeeMap = new Map<string, any>();

        selectedDaySessions.forEach((session: any) => {
            const userId = session.userId;
            const hours = Number(session.totalHours ?? 0);

            if (!employeeMap.has(userId)) {
                employeeMap.set(userId, {
                    ...session,
                    id: session.id ?? userId,
                    userId,
                    userName: session.userName ?? "Unknown Employee",
                    totalHours: 0,
                    exceedsDailyLimit: false,
                });
            }

            const employee = employeeMap.get(userId);

            employee.totalHours += hours;

            // Preserve the earliest sign-in time.
            if (
                new Date(session.signInTime).getTime() <
                new Date(employee.signInTime).getTime()
            ) {
                employee.signInTime = session.signInTime;
            }

            // Preserve the latest available sign-out time.
            if (
                session.signOutTime &&
                (!employee.signOutTime ||
                    new Date(session.signOutTime).getTime() >
                    new Date(employee.signOutTime).getTime())
            ) {
                employee.signOutTime = session.signOutTime;
            }
        });

        return Array.from(employeeMap.values()).map((employee) => ({
            ...employee,
            exceedsDailyLimit: employee.totalHours > 12,
        }));
    }, [workHours, selectedDate]);

    const totalWorkHours = useMemo(() => {
        return dailyTableRows.reduce((total: number, item:any) =>
            total + Number(item.totalHours ?? 0),
                0
        );
    }, [dailyTableRows])

    const formatTime = (
        value: string | null
    ) => {

        if (!value) {
            return "-";
        }

        return new Date(
            value
        ).toLocaleTimeString(
            "en-US",
            {
                hour: "numeric",
                minute: "2-digit",
            }
        );
    };

    const toDateTimeLocal = (value?: string | null) => {
        if (!value) return "";

        const date = new Date(value);

        const offset = date.getTimezoneOffset() * 60_000;

        return new Date(date.getTime() - offset)
            .toISOString()
            .slice(0, 16);
    };

    const getSessionsForDay = (date: string) => {
        if (!Array.isArray(workHours)) return [];

        return workHours.filter((session: any) => {
            if (!session.signInTime) return false;

            return (
                new Date(session.signInTime).toISOString().slice(0, 10) === date
            );
        });
    };

    const formatHours = (
        decimalHours: number
    ) => {

        if (!decimalHours) {
            return "-";
        }

        const totalMinutes =
            Math.round(
                decimalHours * 60
            );

        const hours =
            Math.floor(
                totalMinutes / 60
            );

        const minutes =
            totalMinutes % 60;

        return `${hours}h ${minutes}m`;
    };

    const totalHoursDisplay = formatHours(totalWorkHours);

    const onSelectHoursEdit = (session: any) => {
        console.log("Selected Session:", session);

        const sessionId = session?.id;

        if (!sessionId) {
            toast.error("Work session ID is missing from the API response.");
            return;
        }

        setEditingSessionId(session.id);
        setEditingSession(session);

        setSignInTime(toDateTimeLocal(session.signInTime));
        setSignOutTime(toDateTimeLocal(session.signOutTime));

        setOpenHoursEditor(true);
    }

    const onEditTimeHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();


        if (!userInfo) {
            toast.error("You must be an administrator to edit this project now!");
            return;
        }

        if (!editingSessionId) {
            toast.error("Working session ID is missing");
        }

        if (!signInTime || !signOutTime) {
            toast.error("Please enter both clock-in and clock-out times.");
            return;
        }

        const signInEmployee = new Date(signInTime).toISOString();
        const signOutEmployee = new Date(signOutTime).toISOString();

        if (signOutEmployee <= signInEmployee) {
            toast.error("Clock-out must be later than clock-in");
            return;
        }

        try {
            await updateHours({
                sessionId: editingSessionId,
                signInTime: signInEmployee,
                signOutTime: signOutEmployee,
                userName: userInfo.userName,
                userId: userInfo.id,
                projectId: id,
            }).unwrap();

            toast.success("Employee work hours were successfully updated");

            setOpenHoursEditor(false);
            setEditingSessionId(null);
            setEditingSession(null);

            await refetch();

        } catch (error: any) {
            console.error("Updating employee hours failed:", error);

            toast.error(
                error.message,

            )
        }
    }

    {/* Pagination */}
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(6);

    const totalItems = dailyTableRows?.length;

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentAssignedEmployees = dailyTableRows?.slice(
        indexOfFirstItem,
        indexOfLastItem
    );

// Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, []);

// Keep current page valid when records change
    useEffect(() => {
        setCurrentPage((page) =>
            Math.min(page, Math.max(totalPages, 1))
        );
    }, [totalPages]);


    return (
        <main>

            <Helmet>

                <title>
                    Employees Daily TimeSheet
                </title>

                <meta
                    name="description"
                    content="Employee management and attendance"
                />
            </Helmet>

            {isLoading ? (
                <CustomLoaderSmall />
            ) : (
                <motion.div
                    initial={{
                        opacity: 0,
                        y: 20,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        delay: 0.1,
                    }}
                >

                    <div
                        className="
                        bg-[#0A0A0A]
                        text-white
                        min-h-dvh
                        flex
                    ">

                        {/* ==================================================
                        Sidebar
                    ================================================== */}
                        <div className={"hidden lg:block"}>
                            <NavigationSideBar />
                        </div>


                        {/* ==================================================
                        Main Content
                    ================================================== */}

                        <div
                            className="
                            flex-1
                            flex
                            min-w-0
                            flex-col
                        ">
                            <DashboardHeader projectId={id} />

                            <main
                                className="
                            w-full
                            max-w-[1600px]
                            mx-auto

                            px-4
                            sm:px-6
                            md:px-8
                            lg:px-10

                            py-5
                            sm:py-6
                            md:py-8

                            pb-[env(safe-area-inset-bottom)]
                            "
                            >

                                {/* ==================================================
                                Employee Header
                            ================================================== */}

                                <section
                                    className="
                                    flex
                                    flex-col
                                    lg:flex-row
                                    lg:items-center
                                    lg:justify-between
                                    gap-4
                                    mb-6
                                "
                                >

                                    <div>

                                        <div
                                            className="
                                            flex
                                            items-center
                                            gap-3
                                        "
                                        >

                                            <div
                                                className="
                                           flex
                                           h-11
                                           w-11
                                           shrink-0
                                           items-center
                                           justify-center
                                           rounded-full
                                         bg-blue-500/10
                                           border
                                         border-blue-500/20
                                            "
                                            >

                                                <Users
                                                    size={24}
                                                    className="
                                                    text-blue-500
                                                "
                                                />

                                            </div>


                                            <div>

                                                <h1
                                                    className="
                                                    text-2xl
                                                    font-bold
                                                    text-white
                                                "
                                                >
                                                    Employee Daily Hours
                                                </h1>

                                                <p
                                                    className="
                                                    text-white
                                                    text-sm
                                                    mt-1
                                                "
                                                >
                                                    Manage employees,
                                                    attendance,
                                                    and project assignments
                                                </p>

                                            </div>

                                        </div>

                                    </div>

                                    <div className={"justify-center flex flex-row gap-4"}>
                                        <button
                                            type="button"
                                            onClick={() => setIsAttendanceChatOpen(true)}
                                            aria-label="Open attendance assistant"
                                            title="Ask the attendance assistant"
                                            className="
                                                        flex items-center justify-center gap-2
                                                        rounded-xl border border-gray-700
                                                        px-3 py-2 text-blue-400
                                                        transition hover:bg-gray-800 hover:text-blue-300"
                                        >
                                            <MessageCircleMore size={28} />
                                            <span className="hidden sm:inline text-sm">
                                                Chat
                                            </span>
                                        </button>

                                        <button
                                            onClick={handleExport}
                                            className="
                                        flex
                                        items-center
                                        justify-center
                                        gap-2
                                        px-4
                                        py-2
                                        bg-blue-600
                                        hover:bg-blue-500
                                        rounded-lg
                                        text-sm
                                        font-medium
                                        transition-colors
                                    "
                                        >
                                            <Download size={17} />

                                            Export Timesheet
                                        </button>

                                    </div>


                                </section>

                                {/* ==================================================
                                Summary Cards
                            ================================================== */}
                                <div
                                    className="
                                    grid
                                    grid-cols-1
                                    sm:grid-cols-2
                                    md:grid-cols-3
                                    lg:grid-cols-4
                                    gap-4
                                    mb-6
                                "
                                >
                                    <StackCard
                                        icon={Users}
                                        name="Total Employees"
                                        value={
                                            dailyTableRows.length
                                        }
                                        color="#6366F1"
                                    />


                                    <StackCard
                                        icon={FolderKanban}
                                        name="Total Hours"
                                        value={
                                            totalHoursDisplay
                                        }
                                        color="#06B6D4"
                                    />
                                </div>

                                {/* ==================================================
                                Search / Filters
                            ================================================== */}

                                <div
                                    className="
                                    bg-[#101010]
                                    border
                                    border-gray-800
                                    rounded-lg
                                    p-4
                                    mb-6
                                "
                                >

                                    <div
                                        className="
                                        flex
                                        flex-col
                                        md:flex-row
                                        lg:items-center
                                        justify-between
                                        gap-4
                                    "
                                    >

                                        {/* Search */}

                                        <div
                                            className="
                                            relative
                                            w-full
                                            lg:max-w-md
                                        "
                                        >
                                            <Search
                                                size={18}
                                                className="
                                                absolute
                                                left-3
                                                top-1/2
                                                -translate-y-1/2
                                                text-gray-500
                                            "
                                            />

                                            <input
                                                type="text"
                                                value={
                                                    searchTerm
                                                }
                                                onChange={(
                                                    event
                                                ) => {

                                                    setSearchTerm(
                                                        event.target.value
                                                    );

                                                    setCurrentPage(
                                                        1
                                                    );
                                                }}
                                                placeholder="
                                                Search employee,
                                                email, or project...
                                            "
                                                className="
                                                w-full
                                                bg-[#0A0A0A]
                                                border
                                                border-gray-800
                                                rounded-lg
                                                py-2.5
                                                pl-10
                                                pr-4
                                                text-sm
                                                text-white
                                                placeholder:text-gray-600
                                                focus:outline-none
                                                focus:border-blue-500
                                            "
                                            />

                                        </div>


                                        {/* Filters */}

                                        <div
                                            className="
                                            flex
                                            flex-col
                                            sm:flex-row
                                            gap-3
                                        "
                                        >

                                            <select
                                                value={
                                                    statusFilter
                                                }
                                                onChange={(
                                                    event
                                                ) => {

                                                    setStatusFilter(
                                                        event
                                                            .target
                                                            .value as
                                                            | "All"
                                                            | "Working"
                                                            | "Off"
                                                    );

                                                    setCurrentPage(
                                                        1
                                                    );
                                                }}
                                                className="
                                                bg-[#0A0A0A]
                                                border
                                                border-gray-800
                                                rounded-lg
                                                px-4
                                                py-2.5
                                                text-sm
                                                text-gray-300
                                                focus:outline-none
                                                focus:border-blue-500
                                            "
                                            >

                                                <option value="All">
                                                    All Status
                                                </option>

                                                <option value="Working">
                                                    Working
                                                </option>

                                                <option value="Off">
                                                    Not Working
                                                </option>

                                            </select>
                                        </div>
                                    </div>

                                </div>

                                {/* } Week Navigation
                            ================================================== */}

                                <div className="
                                bg-[#101010]
                                border
                                border-gray-800
                                rounded-lg
                                p-4
                                mb-6
                            ">

                                    <div className="
                                    flex
                                    flex-col
                                    md:flex-row
                                    items-center
                                    justify-between
                                    gap-4
                                ">


                                        <button
                                            onClick={handlePreviousDay}
                                            className="
                                            flex
                                            items-center
                                            gap-2
                                            px-4
                                            py-2
                                            bg-gray-800
                                            hover:bg-gray-700
                                            rounded-lg
                                            text-sm
                                            transition-colors
                                        "
                                        >
                                            <ChevronLeft size={17} />

                                            Previous Day
                                        </button>


                                        <div className={"flex flex-col gap-2"}>
                                            <div className="text-center">

                                                <p className="
                                            text-white
                                            text-lg
                                            font-semibold
                                        ">
                                                    {formattedSelectedDate}
                                                </p>

                                            </div>
                                            {selectedDate !== (() => {
                                                const today = new Date();

                                                const year = today.getFullYear();
                                                const month = String(today.getMonth() + 1).padStart(2, "0");
                                                const day = String(today.getDate()).padStart(2, "0");

                                                // Return to today's date
                                                return `${year}-${month}-${day}`;

                                            }) () && (
                                                <button type={"button"}
                                                        onClick={handleCurrentDay}
                                                        className="text-sm font-medium text-blue-400 hover:text-blue-300">
                                                    Return To Today
                                                </button>
                                            )}
                                        </div>

                                        <button
                                            onClick={handleNextDay}
                                            className="
                                            flex
                                            items-center
                                            gap-2
                                            px-4
                                            py-2
                                            bg-gray-800
                                            hover:bg-gray-700
                                            rounded-lg
                                            text-sm
                                            transition-colors
                                        "
                                        >
                                            Next Day

                                            <ChevronRight size={17} />
                                        </button>

                                    </div>

                                </div>

                                {/* ==================================================
                                Employee Table
                            ================================================== */}

                                <div
                                    className="
                                    bg-[#101010]
                                    border
                                    border-gray-800
                                    rounded-lg
                                    overflow-hidden
                                    mb-6
                                "
                                >

                                    {/* Table Header */}

                                    <div
                                        className="
                                        p-4
                                        border-b
                                        border-gray-800
                                    "
                                    >

                                        <div
                                            className="
                                            flex
                                            flex-col
                                            sm:flex-row
                                            sm:items-center
                                            sm:justify-between
                                            gap-2
                                        "
                                        >

                                            <div className={"flex flex-row justify-items-center gap-x-2"}>

                                                <h2
                                                    className="
                                                    text-lg
                                                    font-semibold
                                                    text-white
                                                "
                                                >
                                                    TimeSheet
                                                </h2>

                                            </div>

                                            <h2
                                                className="
                                                    text-lg
                                                    font-semibold
                                                    text-white
                                                "
                                            >
                                                {activeProjectName}
                                            </h2>
                                        </div>

                                    </div>


                                    {/* Table */}

                                    <div
                                        className="
                                        overflow-x-auto"
                                    >

                                        <table
                                            className="
                                            w-full
                                            text-left
                                            min-w-[1050px]
                                        "
                                        >

                                            <thead
                                                className="
                                                bg-gray-900/50
                                            "
                                            >
                                            <tr>

                                                <th
                                                    className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                >
                                                    Employee Name
                                                </th>


                                                <th
                                                    className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                >
                                                    Status
                                                </th>


                                                <th
                                                    className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                >
                                                    Sign In
                                                </th>


                                                <th
                                                    className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                >
                                                    Sign Out
                                                </th>

                                                <th
                                                    className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                >
                                                    Hours
                                                </th>

                                                <th
                                                    className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                >
                                                    Site
                                                </th>

                                                <th
                                                    className="
                                                        p-4
                                                        text-xs
                                                        font-semibold
                                                        text-gray-400
                                                        uppercase
                                                    "
                                                >
                                                    Device
                                                </th>

                                                <th
                                                    className="
                                                        p-4
                                                    "
                                                />
                                            </tr>

                                            </thead>

                                            <tbody>

                                            {currentAssignedEmployees?.map(
                                                (
                                                    employee
                                                ) => (

                                                    <motion.tr
                                                        key={
                                                            employee.id
                                                        }
                                                        initial={{
                                                            opacity: 0,
                                                        }}
                                                        animate={{
                                                            opacity: 1,
                                                        }}

                                                        className="
                                                            border-t
                                                            border-gray-800
                                                            hover:bg-gray-900/60
                                                            cursor-pointer
                                                            transition-colors
                                                        "
                                                    >

                                                        {/* Employee */}

                                                        <td
                                                            className="
                                                                p-4
                                                            "
                                                        >

                                                            <div
                                                                className="
                                                                    flex
                                                                    items-center
                                                                    gap-2
                                                                "
                                                            >
                                                                <div
                                                                    className='h-12 w-12 rounded-full bg-gradient-to-r from-purple-600 to-blue-500 flex items-center justify-center text-white font-semibold'>

                                                                    {employee?.userName?.split(" ")
                                                                        ?.map(name => name[0])
                                                                        ?.slice(0, 10)
                                                                        ?.join("")
                                                                        ?.toUpperCase()}
                                                                </div>

                                                                <div>

                                                                    <p
                                                                        className="
                                                                            text-gray-200
                                                                            font-medium
                                                                        " >
                                                                        { employee.userName }
                                                                    </p>

                                                                </div>

                                                            </div>

                                                        </td>


                                                        {/* Status */}

                                                        <td
                                                            className="
                                                                p-4
                                                            "
                                                        >

                                                            <span
                                                                className={`
inline-flex
items-center
gap-2
px-2
py-1
text-xs
font-medium
rounded-full

${
                                                                    employee.status ===
                                                                    "Working"
                                                                        ? `
                                                                                bg-green-500/10
                                                                                text-green-400
                                                                                border
                                                                                border-green-500/20
                                                                            `
                                                                        : `
                                                                                bg-gray-500/10
                                                                                text-gray-500
                                                                                border
                                                                                border-gray-500/20
                                                                            `
                                                                }
`}
                                                            >

                                                                <span
                                                                    className={`
w-1.5
h-1.5
rounded-full

${
                                                                        employee.status ===
                                                                        "Working"
                                                                            ? "bg-green-400"
                                                                            : "bg-gray-500"
                                                                    }
`}
                                                                />

                                                                {
                                                                    employee.status ===
                                                                    "Working"
                                                                        ? "Working"
                                                                        : "Not Working"
                                                                }

                                                            </span>

                                                        </td>


                                                        {/* Project */}

                                                        <td
                                                            className="
                                                                p-4
                                                            "
                                                        >

                                                            {employee.signInTime ? (

                                                                <div>

                                                                    <p
                                                                        className="
                                                                            text-gray-200
                                                                            font-medium
                                                                        "
                                                                    >
                                                                        {
                                                                            formatTime(employee.signInTime)
                                                                        }
                                                                    </p>

                                                                </div>

                                                            ) : (

                                                                <span
                                                                    className="
                                                                        text-gray-600
                                                                    "
                                                                >
                                                                    —
                                                                </span>

                                                            )}

                                                        </td>


                                                        {/* Sign In */}

                                                        <td
                                                            className="
                                                                p-4
                                                                text-gray-300
                                                            "
                                                        >

                                                            <div
                                                                className="
                                                                    flex
                                                                    items-center
                                                                    gap-2
                                                                "
                                                            >

                                                                <Clock3
                                                                    size={15}
                                                                    className="
                                                                        text-gray-500
                                                                    "
                                                                />

                                                                {
                                                                    formatTime(
                                                                        employee.signOutTime
                                                                    )
                                                                }

                                                            </div>

                                                        </td>


                                                        {/* Total Hours */}
                                                        <td className={"p-4 text-center"}>
                                                            <div className={"p-4 text-center"}>
                                                                <div className={"flex flex-col items-center gap-1"}>
                                                                    <span className={`text-sm font-semibold ${
                                                                        employee.exceedsDailyLimit ? "text-red-500" : "text-white"
                                                                    }`}>
                                                                        {formatHours(Number(employee?.totalHours ?? 0))}
                                                                    </span>

                                                                    {employee.exceedsDailyLimit && (
                                                                        <span className={"inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2 text-sm font-medium text-red-500"}>
                                                                            <Clock3 size={14} />
                                                                            Exceeds 12h limit
                                                                        </span>
                                                                    )}


                                                                </div>
                                                            </div>
                                                        </td>

                                                        {/* Site */}

                                                        <td
                                                            className="
                                                                p-4
                                                            "
                                                        >

                                                            {employee.siteCode ? (

                                                                <div
                                                                    className="
                                                                        flex
                                                                        items-center
                                                                        gap-2
                                                                        text-gray-300
                                                                    "
                                                                >

                                                                    <MapPin
                                                                        size={15}
                                                                        className="
                                                                            text-gray-500
                                                                        "
                                                                    />

                                                                    {
                                                                        employee.siteCode
                                                                    }

                                                                </div>

                                                            ) : (

                                                                <span
                                                                    className="
                                                                        text-gray-600
                                                                    "
                                                                >
                                                                    —
                                                                </span>

                                                            )}

                                                        </td>


                                                        {/* Device */}

                                                        <td
                                                            className="
                                                                p-4
                                                            "
                                                        >

                                                            {employee.deviceId ? (

                                                                <div
                                                                    className="
                                                                        flex
                                                                        items-center
                                                                        gap-2
                                                                        text-gray-300
                                                                    "
                                                                >

                                                                    <Smartphone
                                                                        size={15}
                                                                        className="
                                                                            text-gray-500
                                                                        "
                                                                    />

                                                                    {
                                                                        employee.deviceId
                                                                    }

                                                                </div>

                                                            ) : (

                                                                <span
                                                                    className="
                                                                        text-gray-600
                                                                    "
                                                                >
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>

                                                        <td className="p-3">
                                                            <div className="flex flex-wrap gap-2">
                                                                {getSessionsForDay(selectedDate)
                                                                    .filter((session: any) => session.userId === employee.userId)
                                                                    .map((session: any) => (
                                                                    <button
                                                                        key={session.id}
                                                                        type="button"
                                                                        onClick={(event) => {
                                                                            event.stopPropagation();
                                                                            onSelectHoursEdit(session);
                                                                        }}
                                                                        className="inline-flex gap-2 items-center text-white rounded-lg p-1"
                                                                    >
                                                                        <MoreVertical size={18} />
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </td>

                                                    </motion.tr>

                                                )
                                            )}

                                            </tbody>

                                            {openHoursEditor && (
                                                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
                                                    <form
                                                        onSubmit={onEditTimeHandler}
                                                        className="w-full max-w-md space-y-5 rounded-2xl border border-gray-700 bg-[#101010] p-6"
                                                    >
                                                        <div>
                                                            <h2 className="text-xl font-bold text-white">
                                                                Edit Employee Hours
                                                            </h2>

                                                            <p className="mt-1 text-sm text-gray-400">
                                                                Correct the recorded attendance times.
                                                            </p>
                                                        </div>

                                                        <div>
                                                            <label className="mb-2 block text-sm text-gray-300">
                                                                Clock In
                                                            </label>

                                                            <input
                                                                type="datetime-local"
                                                                value={signInTime}
                                                                onChange={(e) => setSignInTime(e.target.value)}
                                                                required
                                                                className="w-full rounded-lg border border-gray-700 bg-gray-900 p-3 text-white"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="mb-2 block text-sm text-gray-300">
                                                                Clock Out
                                                            </label>

                                                            <input
                                                                type="datetime-local"
                                                                value={signOutTime}
                                                                onChange={(e) => setSignOutTime(e.target.value)}
                                                                required
                                                                className="w-full rounded-lg border border-gray-700 bg-gray-900 p-3 text-white"
                                                            />
                                                        </div>

                                                        <div className="flex gap-3">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setOpenHoursEditor(false);
                                                                    setEditingSessionId(null);
                                                                    setEditingSession(null);
                                                                }}
                                                                className="flex-1 rounded-lg bg-gray-800 px-4 py-3 text-white"
                                                            >
                                                                Cancel
                                                            </button>

                                                            <button
                                                                type="submit"
                                                                disabled={isUpdateHours}
                                                                className="flex-1 rounded-lg bg-blue-600 px-4 py-3 font-semibold text-white disabled:opacity-50"
                                                            >
                                                                {isUpdateHours ? "Saving..." : "Save Changes"}
                                                            </button>
                                                        </div>
                                                    </form>
                                                </div>
                                            )}

                                        </table>

                                        {/* Desktop Pagination Button */}
                                        {/* Pagination */}
                                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-800 p-4">

                                            {/* Records information */}
                                            <div className="text-sm text-gray-400">
                                                Showing{" "}
                                                {totalItems === 0 ? 0 : indexOfFirstItem + 1}
                                                {" - "}
                                                {Math.min(indexOfLastItem, totalItems)}
                                                {" of "}
                                                {totalItems} employees
                                            </div>

                                            {/* Items per page */}
                                            <div className="flex items-center gap-2">
                                                <label
                                                    htmlFor="itemsPerPage"
                                                    className="text-sm text-gray-400"
                                                >
                                                    Rows per page:
                                                </label>

                                                <select
                                                    id="itemsPerPage"
                                                    value={itemsPerPage}
                                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                                    className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
                                                >
                                                    <option value={5}>5</option>
                                                    <option value={6}>6</option>
                                                    <option value={10}>10</option>
                                                    <option value={20}>20</option>
                                                    <option value={50}>50</option>
                                                </select>
                                            </div>

                                            {/* Page navigation */}
                                            <div className="flex items-center gap-2">

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                                                    }
                                                    disabled={currentPage === 1 || totalItems === 0}
                                                    className="flex items-center gap-1 rounded-lg bg-gray-800 px-3 py-2 text-sm text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    <ChevronLeft size={16} />
                                                    Prev
                                                </button>

                                                <span className="min-w-[90px] text-center text-sm text-gray-300">
            Page {totalItems === 0 ? 0 : currentPage} of {totalPages}
        </span>

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setCurrentPage((prev) =>
                                                            Math.min(prev + 1, totalPages)
                                                        )
                                                    }
                                                    disabled={currentPage >= totalPages || totalItems === 0}
                                                    className="flex items-center gap-1 rounded-lg bg-gray-800 px-3 py-2 text-sm text-white transition hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                                                >
                                                    Next
                                                    <ChevronRight size={16} />
                                                </button>

                                            </div>
                                        </div>


                                    </div>


                                    {/* ==================================================
                                    Pagination
                                ================================================== */}



                                </div>

                            </main>

                        </div>

                        <AttendanceChatWindow open={isAttendanceChatOpen} onClose={() => setIsAttendanceChatOpen(false)} />
                    </div>

                </motion.div>
            )}
        </main>
    );
}


